import { useState } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import Input from "src/components/Input";
import Select from "src/components/Select";
import Radio from "src/components/Radio";
import AddressLookup from "src/components/AddressLookup";
import { getGenders, getTitles } from "src/models/fields";
import { setDefaultValues } from "src/utils";

const defaultValues = {
  Title: "",
  FirstForename: "",
  Surname: "",
  OtherForenames: "",
  Sex: "",
  StudentDetailUserDefined34: "",
  DateOfBirth: "",
  Email: "",
  Email2: "",
  MobileTel: "",
  Address: false,
  AddressLookup: "",
  Postcode: "",
  Address1: "",
  Address2: "",
  Address3: "",
  Address4: "",
};

const PersonalDetails = ({ type, data, onSubmit: onSubmitSuccess }) => {
  const titles = useQuery("titles", getTitles);
  const genders = useQuery("genders", getGenders);

  const {
    control,
    register,
    handleSubmit,
    formState,
    watch,
    setError,
    setValue,
    clearErrors,
  } = useForm({
    mode: "all",
    defaultValues: setDefaultValues(defaultValues, data),
  });
  const { errors, isSubmitting } = formState;

  const email = watch("Email");

  const [addressLookup, setAddressLookup] = useState(!data?.Address1);

  const setAddress = (address) => {
    setValue("Address1", address?.line_1);
    setValue(
      "Address2",
      address?.line_2 !== "" ? address.line_2 : address?.locality
    );
    setValue("Address3", address?.town_or_city);
    setValue("Address4", address?.county);
    setValue("Postcode", address?.postcode);

    clearErrors("AddressLookup");
    setAddressLookup(false);
  };

  const clearAddress = () => {
    setValue("Address1", "");
    setValue("Address2", "");
    setValue("Address3", "");
    setValue("Address4", "");
    setValue("Postcode", "");

    setAddressLookup(true);
  };

  const onSubmit = (data) => {
    if (!data?.Address1) {
      setError("AddressLookup", {
        type: "manual",
        message: "Please enter an address.",
      });
    } else {
      onSubmitSuccess(data);
    }
  };

  return (
    <>
      <h2>Personal Details</h2>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-12 col-md-4">
            <Select
              label="Title"
              name="Title"
              control={control}
              isLoading={titles.isLoading}
              options={titles.data}
              placeholder="Please select a title"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-6">
            <Input
              label="First Name"
              name="FirstForename"
              autoComplete="given-name"
              register={register}
              validation={{
                required: "First name is required",
                maxLength: {
                  value: 50,
                  message: "Maximum 50 characters long",
                },
              }}
              error={errors.FirstForename}
            />
          </div>
          <div className="col-12 col-md-6">
            <Input
              label="Surname"
              name="Surname"
              autoComplete="family-name"
              register={register}
              validation={{
                required: "Surname is required",
                maxLength: {
                  value: 30,
                  message: "Maximum 30 characters long",
                },
              }}
              error={errors.Surname}
            />
          </div>

          <div className="col-12 col-md-6">
            <Input
              label="Other Names"
              name="OtherForenames"
              autoComplete="additional-name"
              register={register}
              validation={{
                maxLength: {
                  value: 30,
                  message: "Maximum 30 characters long",
                },
              }}
              error={errors.OtherForenames}
            />
          </div>
          <div className="col-12 col-md-6">
            <Input
              type="tel"
              label="Mobile Phone"
              name="MobileTel"
              register={register}
              autoComplete="tel"
              validation={{
                required: "Phone number is required",
                pattern: {
                  value: /^[0-9]{11}$/,
                  message:
                    "Please enter 11 digit UK number, without any special characters.",
                },
                maxLength: {
                  value: 35,
                  message: "Maximum 35 characters long",
                },
              }}
              error={errors.MobileTel}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-6">
            <Radio
              label="Sex"
              name="Sex"
              options={[
                { label: "Male", value: "M" },
                { label: "Female", value: "F" },
                { label: "Rather Not Say", value: "R" },
              ]}
              autoComplete="sex"
              inline
              register={register}
              validation={{ required: "Sex is required" }}
              error={errors.Sex}
            />
          </div>
          <div className="col-12 col-md-6">
            <Select
              label="Gender"
              name="StudentDetailUserDefined34"
              control={control}
              isLoading={genders.isLoading}
              options={genders.data}
              placeholder="Please select a gender"
              info="Gender identity is a way to describe how someone feels about their gender. For example, some people may identify as a male or a female, while others may find neither of these terms feel right for them, and identify as neither or somewhere in the middle."
              error={errors.StudentDetailUserDefined34}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-6">
            <Input
              type="email"
              label="Email Address"
              name="Email"
              autoComplete="email"
              register={register}
              description="Use your personal email rather than your School email"
              validation={{
                required: "Email Address is required",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Please enter a valid email address",
                },
                validate: (value) => {
                  if (type === "enrol") return true;

                  if (checkSchoolEmails(value)) {
                    return "Please use your personal email rather than your School email";
                  }
                },
              }}
              error={errors.Email}
            />
          </div>
          <div className="col-12 col-md-6">
            <Input
              type="email"
              label="Confirm Email Address"
              name="Email2"
              autoComplete="off"
              register={register}
              validation={{
                required: "Email Address is required",
                validate: (value) =>
                  value === email || "The email addresses do not match",
              }}
              error={errors.Email2}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-6">
            {addressLookup && (
              <>
                <AddressLookup
                  onSubmit={setAddress}
                  error={errors.AddressLookup}
                />

                <button
                  type="button"
                  onClick={() => setAddressLookup(false)}
                  className="form-link"
                >
                  Enter address manually
                </button>
              </>
            )}
          </div>
          <div className="col-12 col-md-6">
            <Input
              label="Date of Birth"
              name="DateOfBirth"
              placeholder="dd/mm/yyyy"
              min="1901-01-01"
              register={register}
              validation={{
                required: "Date of birth is required",
                pattern: {
                  value:
                    /^(0[1-9]|[12][0-9]|3[01])[/](0[1-9]|1[012])[/](19|20)\d\d$/,
                  message: "Please enter date of birth in format dd/mm/yyyy",
                },
              }}
              error={errors.DateOfBirth}
            />
          </div>
        </div>

        {!addressLookup && (
          <>
            <div className="row">
              <div className="col-12 col-md-6">
                <Input
                  label="Address 1"
                  name="Address1"
                  autoComplete="address-line1"
                  register={register}
                  validation={{ required: "Address line 1 is required." }}
                  error={errors.Address1}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  label="Address 2"
                  name="Address2"
                  autoComplete="address-line2"
                  register={register}
                  error={errors.Address2}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  label="Town"
                  name="Address3"
                  register={register}
                  validation={{ required: "Town is required." }}
                  error={errors.Address3}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  label="County"
                  name="Address4"
                  register={register}
                  error={errors.Address4}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  label="Postcode"
                  name="Postcode"
                  autoComplete="postal-code"
                  register={register}
                  validation={{ required: "Postcode is required." }}
                  error={errors.Postcode}
                />
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={clearAddress}
                className="form-link"
              >
                Search a different address
              </button>
            </div>
          </>
        )}

        <button type="submit" className="form-submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving" : "Next"}
        </button>
      </form>
    </>
  );
};

export default PersonalDetails;

function checkSchoolEmails(value) {
  const excluded = [
    "stantonbury-tove.org.uk",
    "radcliffeschool.org.uk",
    "mket.org.uk",
    "sponne.org.uk",
    "haz5d.com",
    "cedarsupper.co.uk",
    "vandyke.org.uk",
    "ousedale.org.uk",
    "cottesloe.bucks.sch.uk",
    "miltonkeynesacademy.org.uk",
    "lordgrey.org.uk",
  ];

  return excluded.some((domain) => value.endsWith(domain));
}
